import { CSSProperties, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProfileStore } from '@/store/useProfileStore.ts';
import { helpers } from '@/helpers.ts';
import { Toast } from '@/components/Toast/Toast.tsx';
import { MessageTypesUI } from '@/enums/viewEnums.ts';
import {
  AgeRangeFilter,
  MAX_AGE,
  MIN_AGE
} from '@/components/Profile/Settings/SearchFilters/AgeRangeFilter/AgeRangeFilter.tsx';
import { GenderFilter } from '@/components/Profile/Settings/SearchFilters/GenderFilter/GenderFilter.tsx';
import { useDiscoveryStore } from '@/store/useDiscoveryStore.ts';
import { MainButton } from '@/components/common/MainButton/MainButton.tsx';
import { SystemModalContainer } from '@/components/common/SystemModal/SystemModal.tsx';

interface SearchFiltersProps {
  onClose: () => void;
}

const SearchFiltersModal: FC<SearchFiltersProps> = ({ onClose }) => {
  const { t } = useTranslation();

  const { aboutMe, updateSearchFilters } = useProfileStore();
  const { resetExpired, removeAllCandidates, fetchCandidates } =
    useDiscoveryStore();

  const defaultMinAge = aboutMe?.profile.age_filter_min ?? MIN_AGE;
  const defaultMaxAge = aboutMe?.profile.age_filter_max ?? MAX_AGE;
  const defaultGender = aboutMe?.profile.search_gender;

  const [minAge, setMinAge] = useState<number>(defaultMinAge);
  const [maxAge, setMaxAge] = useState<number>(defaultMaxAge);
  const [searchGender, setSearchGender] = useState<string | undefined>(
    defaultGender
  );
  const [isEdited, setIsEdited] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  const handleAgeChange = (newMin: number, newMax: number) => {
    setMinAge(newMin);
    setMaxAge(newMax);
    setIsEdited(true);
  };

  const handleGenderChange = (gender: string) => {
    setSearchGender(gender);
    setIsEdited(true);
  };

  const submitFilters = async () => {
    try {
      setIsSending(true);
      await updateSearchFilters({
        age_filter_min: minAge,
        age_filter_max: maxAge,
        search_gender: searchGender
      });

      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_SUCCESS,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_SUCCESS} />
      });

      resetExpired();
      removeAllCandidates();
      await fetchCandidates(); // added for NoResults screen when extending filters on empty search

      if (onClose) {
        onClose();
      }
    } catch {
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_ERROR,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_ERROR} />
      });
    } finally {
      setIsEdited(false);
      setIsSending(false);
    }
  };

  return (
    <SystemModalContainer
      headerTitle={t('search_filters_menu_item')}
      handleClose={onClose}
    >
      <div style={{ paddingBottom: '60px' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: 14 }}>
          <AgeRangeFilter
            initialMinAge={defaultMinAge}
            initialMaxAge={defaultMaxAge}
            onAgeChange={handleAgeChange}
          />
          <GenderFilter
            initialGender={defaultGender}
            onGenderChange={handleGenderChange}
          />
        </div>

        <div style={comingSoonTextStyle}>
          ✨ {t('search_filters.coming_soon')}
        </div>
      </div>

      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '5px 18px 15px 18px'
        }}
      >
        <MainButton
          onClick={submitFilters}
          disabled={!isEdited}
          loading={isSending}
          text={t('button.save')}
        />
      </div>
    </SystemModalContainer>
  );
};

const comingSoonTextStyle: CSSProperties = {
  textAlign: 'center',
  color: '#999',
  fontSize: '13px',
  padding: '10px',
  fontWeight: 500
};

export default SearchFiltersModal;
